import { Tabbar } from "@telegram-apps/telegram-ui";
import "./AppTabs.css";
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import InviteIcon from "./Icons/InviteIcon";
import CoinsIcon from "./Icons/CoinsIcon";
import ShopIcon from "./Icons/ShopIcon";
import LeaderboardIcon from "./Icons/LeaderboardIcon";
import ManaIcon from "./Icons/ManaIcon";
import { init } from "@telegram-apps/sdk";
import hapticFeedback from "../../utils/haptics";
import { useI18n } from "../../utils/i18n/I18nProvider";
import { useGameStore, selectMana, selectIsInitialized } from "../../store/gameStore";
import { StorageService } from "../../services/LocalStorageService";

export interface AppTabsProps {
  className?: string;
}

export function AppTabs({ className = "" }: AppTabsProps): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useI18n();
  const manaData = useGameStore(selectMana);
  const isInitialized = useGameStore(selectIsInitialized);
  const mana = manaData?.current || 0;

  init();

  // Function to check if notification should be shown
  const shouldShowNotification = useCallback((tabPath: string): boolean => {
    const storageKey = `notification_dismissed_${tabPath}`;
    const dismissedTime = localStorage.getItem(storageKey);

    if (!dismissedTime) return true;

    const dismissedTimestamp = parseInt(dismissedTime, 10);
    const now = Date.now();
    const twentyFourHours = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

    // If more than 24 hours have passed, show notification again
    if (now - dismissedTimestamp > twentyFourHours) {
      localStorage.removeItem(storageKey); // Clean up expired entry
      return true;
    }

    return false;
  }, []);

  // Function to dismiss notification for 24 hours
  const dismissNotification = useCallback((tabPath: string) => {
    const storageKey = `notification_dismissed_${tabPath}`;
    localStorage.setItem(storageKey, Date.now().toString());
  }, []);

  // Mana is now automatically updated via store subscription

  const buttons = [
    {
      text: t("common.frens"),
      icon: <InviteIcon />,
      path: "/invite",
      hasNotification: shouldShowNotification("/invite"),
    },
    { text: t("common.shop"), icon: <ShopIcon />, path: "/shop" },
    {
      text: t("common.mana"),
      icon: <ManaIcon count={mana} />,
      path: "/mana",
      special: mana > 0, // Only special when mana > 0
      // show a one-time red dot on mana tab if not yet dismissed
      hasNotification: !StorageService.isOneTimeHintDismissed(
        StorageService.ONE_TIME_HINT_KEYS.MANA_TAB
      ),
    },
    {
      text: t("common.tasks"),
      icon: <CoinsIcon />,
      path: "/tasks",
      hasNotification: shouldShowNotification("/tasks"),
    },
    { text: t("common.top"), icon: <LeaderboardIcon />, path: "/leaderboard" },
    // { text: t("common.gifts"), icon: <AirdropIcon />, path: "/airdrop" },
  ];

  const handleTabClick = (path: string) => {
    hapticFeedback.impactOccurred("light");

    // Dismiss time-based notification when tab is clicked (except for mana)
    if (path !== "/mana") {
      dismissNotification(path);
    }

    // Dismiss one-time mana tab hint if clicking mana
    if (path === "/mana") {
      StorageService.dismissOneTimeHint(
        StorageService.ONE_TIME_HINT_KEYS.MANA_TAB
      );
    }

    navigate(path);
  };

  return (
    <>
      <Tabbar className={`app-tabs ${className}`}>
        {buttons.map((button, index) => (
          <Tabbar.Item
            key={index}
            className={`tab-button ${
              location.pathname === button.path ? "tab-button--active" : ""
            } ${button.text === t("common.mana") ? "tab-button--mana" : ""} ${
              button.special ? "tab-button--special" : ""
            }`}
            text={button.text}
            onClick={() => handleTabClick(button.path)}
          >
            {button.hasNotification ? (
              <div className="tab-icon-container">
                {button.icon}
                <div className="notification-dot"></div>
              </div>
            ) : (
              button.icon
            )}
          </Tabbar.Item>
        ))}
      </Tabbar>
    </>
  );
}
